import { useEffect, useRef, useState, type ReactNode } from 'react';

type DeferredSectionProps = {
  sectionId: string;
  className?: string;
  forceVisible?: boolean;
  children: ReactNode;
};

/**
 * Mounts children only when the section nears the viewport (or when forced
 * via nav). Keeps below-the-fold page chunks off the initial critical path.
 */
export default function DeferredSection({
  sectionId,
  className,
  forceVisible = false,
  children,
}: DeferredSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(forceVisible);

  useEffect(() => {
    if (forceVisible) {
      setVisible(true);
      return;
    }

    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setVisible(true);
        observer.disconnect();
      },
      { rootMargin: '0px 0px 200px 0px' },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
    };
  }, [forceVisible]);

  return (
    <section ref={sectionRef} id={sectionId} className={className}>
      {visible ? children : <div className="min-h-[50vh]" aria-hidden="true" />}
    </section>
  );
}
